// Formats d'impression connus du navigateur : les trois formats historiques + les produits du catalogue
// (declares dans l'admin, Prodigi etape 2b) que le serveur annonce a un visiteur autorise.
//
// UNE seule table de dimensions, partagee par l'atelier, les miniatures, le controle qualite et
// l'apercu : elle remplace les copies qui vivaient dans chaque fichier. Elle est MUTABLE a dessein :
// `registerCatalogFormats` y ajoute les produits recus de GET /orders/formats (nom, dimensions,
// profil de mise en page). Un identifiant inconnu retombe sur Standard, comme avant.
//
// Alignee sur backend/services/composition/coverFormat.js (COVER_FORMATS), source de verite.

export const BUILTIN_FORMAT_IDS = ['livret', 'standard', 'luxe'];

export const FORMAT_DIMENSIONS_MM = {
  livret: { widthMm: 200, heightMm: 200 },
  standard: { widthMm: 210, heightMm: 280 },
  luxe: { widthMm: 210, heightMm: 280 }
};

// Regles de pagination des trois formats historiques (alignees sur backend/services/composition/pageLimits.js).
const BUILTIN_PAGE_LIMITS = { minPages: 30, maxPages: 200, pageStep: 2 };
// identifiant d'un produit du catalogue -> regles de pagination de SON fournisseur
const PAGE_LIMITS = {};

// identifiant d'un produit du catalogue -> format historique dont il emprunte la mise en page
const STYLE_PROFILES = {};
// identifiant -> { nameFr/nom, reliure } pour les libelles
const CATALOG_NAMES = {};

/** Format dont depend la mise en page (densite, typographie) : lui-meme pour les trois historiques. */
export const styleProfileOf = (formatId) => STYLE_PROFILES[formatId] || formatId;

/**
 * LE PLI D'UNE DOUBLE PAGE DEPEND DE LA RELIURE (miroir de backend/services/composition/coverFormat.js) :
 *   dos colle -> recouvrement central de 4 mm et zone de securite de 12 mm autour du pli ;
 *   layflat   -> image continue (0) et aucune zone a eviter (0) : les pages s'ouvrent completement a plat.
 */
export const isLayflatFormat = (formatId) => CATALOG_NAMES[formatId]?.binding === 'layflat';
export const spreadOverlapMmOf = (formatId) => (isLayflatFormat(formatId) ? 0 : 4);
export const gutterSafetyMmOf = (formatId) => (isLayflatFormat(formatId) ? 0 : 12);

/** Dimensions d'un format (Standard si inconnu). */
export const formatDimensions = (formatId) => FORMAT_DIMENSIONS_MM[formatId] || FORMAT_DIMENSIONS_MM.standard;

/** Rapport largeur / hauteur d'une page de ce format. */
export const formatAspectRatio = (formatId) => {
  const { widthMm, heightMm } = formatDimensions(formatId);
  return widthMm / heightMm;
};

/**
 * Regles de pagination d'un format : 30 a 200 pour les formats historiques ; celles du fournisseur pour un
 * produit du catalogue (ex. 18 a 122 pages), annoncees par le serveur. `minPages` est aussi le nombre de
 * pages d'un livre neuf.
 */
export const pageLimits = (formatId) => PAGE_LIMITS[formatId] || BUILTIN_PAGE_LIMITS;

/** Taille en cm avec au plus une decimale (21 x 29,7 ; 21,6 x 27,9), jamais arrondie a l'entier. */
export const formatCm = (mm, lang) => (
  new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: 1 }).format(Number(mm) / 10)
);

export const isKnownFormat = (formatId) => Boolean(FORMAT_DIMENSIONS_MM[formatId]);
export const isCatalogFormat = (formatId) => Boolean(STYLE_PROFILES[formatId]);
export const catalogFormatName = (formatId) => CATALOG_NAMES[formatId] || null;

/** Produits du catalogue connus de ce navigateur (donc autorises pour ce visiteur), dans l'ordre annonce par le serveur. */
export const listCatalogFormats = () => Object.keys(CATALOG_NAMES)
  .map((id) => ({ id, ...CATALOG_NAMES[id], ...FORMAT_DIMENSIONS_MM[id] }))
  .sort((a, b) => a.sortIndex - b.sortIndex);

/**
 * Enregistre les produits du catalogue recus de GET /orders/formats. Les formats historiques sont ignores ;
 * un produit sans dimensions valides aussi. Retourne le nombre de formats nouvellement connus.
 */
export function registerCatalogFormats(formats) {
  let added = 0;
  (Array.isArray(formats) ? formats : []).forEach((format, position) => {
    const id = format?.formatId;
    if (!id || BUILTIN_FORMAT_IDS.includes(id)) return;
    const widthMm = Number(format.widthMm);
    const heightMm = Number(format.heightMm);
    if (!(widthMm > 0 && heightMm > 0)) return;
    if (!FORMAT_DIMENSIONS_MM[id]) added += 1;
    FORMAT_DIMENSIONS_MM[id] = { widthMm, heightMm };
    STYLE_PROFILES[id] = BUILTIN_FORMAT_IDS.includes(format.styleProfile) ? format.styleProfile : 'standard';
    const minPages = Number(format.minPages) > 0 ? Number(format.minPages) : BUILTIN_PAGE_LIMITS.minPages;
    const maxPages = Number(format.maxPages) >= minPages ? Number(format.maxPages) : BUILTIN_PAGE_LIMITS.maxPages;
    PAGE_LIMITS[id] = { minPages, maxPages, pageStep: Number(format.pageStep) > 0 ? Number(format.pageStep) : 2 };
    CATALOG_NAMES[id] = { nom: format.nom || id, reliure: format.reliure || null, binding: format.binding || null, accroche: format.accroche || '', sortIndex: position };
  });
  return added;
}
